// src/encounter/lib/chart-column-model.ts
// PROD-2743 · the non-visual rules of the frame-v2 chart column (`chart-column-v2.tsx`): the
// wayfinding category of each section, the safety-first order, the identity run and what the
// collapsed strip's allergy dot says and the visit status chip's tone. Kept out of the component file so it exports components only.
import type { IpsEntry, IpsSection, PatientHeaderDemographics, StatusChipProps } from '@scripsteam/scrips-signal-ds';
import { pmRailStatusSemantic, pmStatusLabel, pmStatusSemantic } from '@/lib/pm-appointment-status';

/** The closed wayfinding map (SYSTEM-RULES map v.2 §2), per chart section id. */
export type ChartWayfind = 'allergies' | 'problems' | 'medications' | 'history' | 'neutral';
export const CHART_SECTION_WAYFIND: Readonly<Record<string, ChartWayfind>> = {
  allergies: 'allergies',
  flags: 'neutral',
  'active-problems': 'problems',
  'resolved-problems': 'problems',
  medications: 'medications',
  vaccinations: 'neutral',
  procedures: 'history',
  'family-history': 'history',
  'social-history': 'history',
  // Occupation and Education ARE social history (design desk review of #574, 1 Oct), so they take
  // the history ink with Social history. SYSTEM-RULES §2 still lists them under `neutral`; the
  // desk owns that table and was told in the PR.
  occupation: 'history',
  education: 'history',
};
/**
 * Safety first, as in the prototype (Allergies · Active problems · Medications; Antonin, 19 Oct): those three, then
 * the rest in the column's order (flags, resolved problems, the history and social lists). A section the rail hands over that is not listed here keeps its place
 * at the END — nothing the rail has is ever dropped from the column.
 */
export const CHART_SECTION_ORDER: readonly string[] = [
  'allergies',
  'active-problems',
  'medications',
  'flags',
  'resolved-problems',
  'vaccinations',
  'procedures',
  'family-history',
  'social-history',
  'occupation',
  'education',
];

/** The row the summary would render, minus a synthetic "+N more" trailer (never a record). */
export function realEntries(section: IpsSection): IpsEntry[] {
  return section.entries.filter((e) => e.id !== `${section.id}-more`);
}

export const SEX_DISPLAY: Record<string, string> = { M: 'Male', F: 'Female', X: 'Other' };

/** `46y • Male • Married` — the DS person-card identity run (`personCardIdentityRun`: the same
 *  fields, the same U+2022 bullet; D-51, desk addendum on #574), WITHOUT the MRN (it is quiet and
 *  last, below). Marital status stays because the DS run renders it. */
export const IDENTITY_SEPARATOR = ' \u2022 ';
export function identityRun(d: PatientHeaderDemographics | undefined): string | undefined {
  const parts = [
    d?.ageDisplay ?? (d?.age != null ? `${d.age}y` : undefined),
    d?.sex ? (SEX_DISPLAY[d.sex] ?? d.sex) : undefined,
    d?.maritalStatus,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(IDENTITY_SEPARATOR) : undefined;
}

/** What the collapsed 72px strip says about allergies. The dot is ALWAYS drawn (D-41 safety). */
export type AllergyDotState = 'recorded' | 'none-recorded' | 'unavailable' | 'failed' | 'loading';

export function allergyDotState({
  allergies,
  loading,
  loadError,
  unavailable,
}: {
  allergies?: IpsSection;
  loading?: boolean;
  loadError?: boolean;
  unavailable?: boolean;
}): AllergyDotState {
  if (loading) return 'loading';
  if (loadError) return 'failed';
  if (unavailable || !allergies) return 'unavailable';
  return realEntries(allergies).length > 0 ? 'recorded' : 'none-recorded';
}

export function allergyDotLabel(state: AllergyDotState, allergies?: IpsSection): string {
  switch (state) {
    case 'recorded':
      return `Allergies: ${realEntries(allergies!).map((e) => e.primary).join(', ')}`;
    case 'none-recorded':
      return 'No allergies recorded';
    case 'unavailable':
      return 'Allergy information unavailable';
    case 'failed':
      return "Allergies couldn't be loaded";
    case 'loading':
      return 'Loading allergies';
  }
}

/**
 * An ISO 8601 calendar date STRING's parts, read from its text (never through `Date`, which would
 * shift a midnight date across a time zone). Accepts a year (`2024`), a year-month (`2024-05`), a
 * date (`2024-05-02`) and a date-time whose time follows a COMPLETE date (`2024-05-02T00:00:00`,
 * optional fraction and offset). Rejected as no date: a month outside 01–12, a day the month does
 * not have (`2026-02-31`, `2023-02-29`), a time with no date, bad syntax or an impossible clock or
 * offset (`T25:00`, `+99:99`), and a year before 1900
 * (a sentinel such as `0001-01-01`).
 */
const ISO_DATE = /^(\d{4})(?:-(\d{2})(?:-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(?:Z|[+-](\d{2}):?(\d{2}))?)?)?)?$/;
function daysIn(year: number, month: number): number {
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  return [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}
function isoParts(iso: string | null | undefined): { year: number; month?: number; day?: number } | undefined {
  const m = ISO_DATE.exec((iso ?? '').trim());
  if (!m) return undefined;
  const year = Number(m[1]);
  const month = m[2] ? Number(m[2]) : undefined;
  const day = m[3] ? Number(m[3]) : undefined;
  if (year < 1900) return undefined;
  if (month !== undefined && (month < 1 || month > 12)) return undefined;
  if (day !== undefined && (day < 1 || day > daysIn(year, month!))) return undefined;
  // A time is checked too: an impossible clock or offset means the string is not a date.
  const [hh, mm, ss, oh, om] = [m[4], m[5], m[6], m[7], m[8]].map((v) => (v === undefined ? 0 : Number(v)));
  if (hh > 23 || mm > 59 || ss > 59 || oh > 14 || om > 59) return undefined;
  return { year, month, day };
}

/** The onset year beside a term (the `ClinicalTermRow` `onsetYear`). */
export function isoYear(iso: string | null | undefined): number | undefined {
  return isoParts(iso)?.year;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `May 2026` (the `ClinicalTermRow` resolved chip). A year-only date has no month to show. */
export function isoMonthYear(iso: string | null | undefined): string | undefined {
  const p = isoParts(iso);
  return p?.month ? `${MONTH_SHORT[p.month - 1]} ${p.year}` : undefined;
}

/** `12 Oct 2025`, or as much of it as the date holds (`Oct 2025`, `2025`). The left rail's dated rows (vaccinations). */
export function isoDayMonthYear(iso: string | null | undefined): string | undefined {
  const p = isoParts(iso);
  if (!p) return undefined;
  if (p.month && p.day) return `${p.day} ${MONTH_SHORT[p.month - 1]} ${p.year}`;
  return p.month ? `${MONTH_SHORT[p.month - 1]} ${p.year}` : String(p.year);
}

type ChipSemantic = NonNullable<StatusChipProps['semantic']>;
export interface VisitStatusChip {
  label: string;
  semantic: ChipSemantic;
  tone: 'solid' | 'soft';
}

const statusKey = (raw: string) => raw.toLowerCase().replace(/[\s_-]/g, '');
/**
 * The live state of a STARTED visit (the only host that draws this chip). On the wire it is
 * `CheckedIn`: the encounter's start event sets the appointment to CheckedIn (Scrips.
 * AppointmentScheduling `AppointmentStatusIntegrationEventHandler`, CheckedIn on start, Fulfilled
 * on sign), and the DS folds the `InSession` / `In-Session` display alias onto that same code.
 *
 * WORDS: the status FIELD's words, PM's "Checked In" (PROD-3045 N5). This chip used to rename
 * CheckedIn to "In session" while the pre-visit status pill, the Today card and the Calendar read
 * the same field as "Checked In", so one visit showed two statuses (measured on dev, 6 Oct 18:50).
 * The field cannot say "in session": front-desk check-in writes the same CheckedIn code, and the
 * appointment service itself counts CheckedIn as the waiting room (`AppointmentRepository.cs`
 * `WaitingRoom = … CheckedIn`). So every surface now says what the field says. TONE is unchanged:
 * on a started visit this is the live state, the one solid chip (D-49 (3)).
 */
const IN_SESSION = new Set(['insession', 'inprogress', 'checkedin']);
/** Every code PM's bucketing recognises (`appointmentStatusFromCode`). Anything else would fall to
 *  PM's `booked` default, so it keeps its own words instead of being called "Booked". */
const KNOWN = new Set([
  'arrived', 'fulfilled', 'completed', 'cancelled', 'canceled', 'enteredinerror',
  'noshow', 'waitlist', 'waitlisted', 'proposed', 'pending', 'confirmed', 'booked',
]);

/**
 * The STARTED visit's status chip on the patient card (design: `PatientHeader … status`). Colour and
 * words are PM's, the same mapping the pre-visit status pill uses (`pmStatusSemantic` /
 * `pmStatusLabel`), with no rename of its own. Tone is D-49(3): the live state (the CheckedIn
 * family) WAS the one solid chip; since 16 Oct it is soft blue like every other status (Antonin).
 */
export function visitStatusChip(raw: string | null | undefined): VisitStatusChip | undefined {
  const code = raw?.trim();
  if (!code) return undefined;
  const key = statusKey(code);
  // Antonin, 16 Oct, on the live state's green solid chip: "violation of colour, should be soft blue" — it supersedes D-49(3)'s
  // "the ONE solid chip" for the colour and the tone: the same soft blue as the pre-visit pill (`pmRailStatusSemantic`).
  if (IN_SESSION.has(key)) return { label: pmStatusLabel('CheckedIn'), semantic: pmRailStatusSemantic(code), tone: 'soft' };
  return { label: KNOWN.has(key) ? pmStatusLabel(code) : code, semantic: pmStatusSemantic(code), tone: 'soft' };
}

// ── The order of rows WITHIN a section (encounter owner ruling 4, 1 Nov, D-5315; left-rail target 4 Nov §2). Ported unchanged
//    from origin/claude/rail-v3-behaviour (1a1e5283), where it sat behind the `chartRailV3` flag; here it is the rail-v3 rule. ──

/** What a row's full chart record says for sorting, when that record has loaded. Every field is optional: a fact the record
 *  does not carry sorts LAST and never throws. */
export interface ChartSortFacts {
  /** Allergies: the record's recorded date. Active problems: createdDate. Flags: createdDate. Resolved problems:
   *  abatementDate ?? endDate. ISO 8601 date or date-time. */
  date?: string | null;
  /** Allergies only: the criticality code or display (`high` sorts first). */
  criticality?: string | null;
}

/** A date's instant, or undefined when there is none or it does not parse (it then sorts last). */
function sortTime(iso: string | null | undefined): number | undefined {
  const raw = iso?.trim();
  if (!raw) return undefined;
  const t = Date.parse(raw);
  return Number.isNaN(t) ? undefined : t;
}

/** Newest first; a missing date after every dated row. 0 when both are missing (the caller's next rule decides). */
function newestFirst(a: number | undefined, b: number | undefined): number {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  return b - a;
}

const isHighCriticality = (c: string | null | undefined) => (c ?? '').trim().toLowerCase() === 'high';
const byName = (a: IpsEntry, b: IpsEntry) =>
  a.primary.localeCompare(b.primary, undefined, { sensitivity: 'base' });

/**
 * The ruled order within a section (D-5315):
 *  - allergies: high criticality first, then the newest recorded date;
 *  - active problems: the newest createdDate first, ties by name;
 *  - medications: alphabetical, case-insensitive;
 *  - flags: the newest first;
 *  - resolved problems: the newest (abatementDate ?? endDate) first.
 * Any other section keeps the wire order. A row whose record or date is missing sorts after the dated rows, in wire order.
 * The sort is stable, so equal rows keep the wire order. The synthetic "+N more" trailer stays last.
 */
export function sortSection(
  sectionId: string,
  entries: readonly IpsEntry[],
  records?: ReadonlyMap<string, ChartSortFacts>,
): IpsEntry[] {
  const trailer = entries.filter((e) => e.id === `${sectionId}-more`);
  const rows = entries.filter((e) => e.id !== `${sectionId}-more`);
  const fact = (e: IpsEntry) => records?.get(e.id);
  const time = (e: IpsEntry) => sortTime(fact(e)?.date);
  let compare: ((a: IpsEntry, b: IpsEntry) => number) | undefined;
  switch (sectionId) {
    case 'allergies':
      compare = (a, b) => {
        const ha = isHighCriticality(fact(a)?.criticality) ? 0 : 1;
        const hb = isHighCriticality(fact(b)?.criticality) ? 0 : 1;
        return ha - hb || newestFirst(time(a), time(b));
      };
      break;
    case 'active-problems':
      compare = (a, b) => newestFirst(time(a), time(b)) || byName(a, b);
      break;
    case 'medications':
      compare = byName;
      break;
    case 'flags':
    case 'resolved-problems':
      compare = (a, b) => newestFirst(time(a), time(b));
      break;
    default:
      compare = undefined;
  }
  return [...(compare ? [...rows].sort(compare) : rows), ...trailer];
}

/**
 * The session-stable order (D-5315): an id keeps the place it had the first time it was seen; a NEW id goes in at its sorted
 * place, i.e. just before the first already-placed id that the sort puts after it (at the end when there is none); an id no
 * longer present is dropped. `frozen` undefined = first sight: the sorted order itself.
 */
export function mergeStableOrder(frozen: readonly string[] | undefined, sorted: readonly string[]): string[] {
  if (!frozen) return [...sorted];
  const rank = new Map(sorted.map((id, i) => [id, i]));
  const out = frozen.filter((id) => rank.has(id));
  const placed = new Set(out);
  sorted.forEach((id, i) => {
    if (placed.has(id)) return;
    const at = out.findIndex((other) => (rank.get(other) ?? -1) > i);
    out.splice(at < 0 ? out.length : at, 0, id);
    placed.add(id);
  });
  return out;
}

/** `entries` in the order `ids` gives; an entry `ids` does not name keeps its relative place at the end. */
export function orderEntries(entries: readonly IpsEntry[], ids: readonly string[]): IpsEntry[] {
  const rank = new Map(ids.map((id, i) => [id, i]));
  return entries
    .map((e, i) => ({ e, i, r: rank.get(e.id) ?? ids.length + i }))
    .sort((x, y) => x.r - y.r)
    .map((x) => x.e);
}
